/* src/modules/office/components/OfficeAttendanceReport.js */
import React, { useState, useMemo } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { SearchIcon } from '../../../components/icons/Icons';
import { getDatesInRange, isWeekend, getDayName } from '../utils/dateHelpers';

export default function OfficeAttendanceReport({ userProfile, appId, isManager }) {
    const [reportData, setReportData] = useState([]);
    const [filterStart, setFilterStart] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
    const [filterEnd, setFilterEnd] = useState(new Date().toISOString().split('T')[0]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const generateReport = async () => {
        setLoading(true);
        try {
            // 1. Fetch Users in Scope
            let targetUsers = [];
            
            const isHRAdmin = userProfile.role === 'Admin' || userProfile.department === 'HR';

            if (isHRAdmin) {
                // ✅ FIX: Fetch ALL users (Removed 'where status == Approved')
                // We need Deactivated users too, in case they worked during the selected period.
                const qUsers = query(collection(db, `artifacts/${appId}/users`));
                const snap = await getDocs(qUsers);
                targetUsers = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            } else if (isManager) {
                const qUsers = query(collection(db, `artifacts/${appId}/users`), where("managerId", "==", userProfile.id));
                const snap = await getDocs(qUsers);
                targetUsers = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                targetUsers.push(userProfile); 
            } else {
                targetUsers = [userProfile];
            }

            // 2. Fetch Schedules
            const schedRef = collection(db, `artifacts/${appId}/office_schedules`);
            const schedSnap = await getDocs(schedRef);
            const allSchedules = schedSnap.docs.map(d => d.data());

            // 3. Fetch Deviations
            let allDeviations = [];
            const devRef = collection(db, `artifacts/${appId}/schedule_deviations`);

            if (isHRAdmin) {
                const snap = await getDocs(devRef);
                allDeviations = snap.docs.map(d => d.data());
            } else if (isManager) {
                const qTeam = query(devRef, where("managerId", "==", userProfile.id));
                const qSelf = query(devRef, where("userId", "==", userProfile.id));
                const [snapTeam, snapSelf] = await Promise.all([getDocs(qTeam), getDocs(qSelf)]);
                const devMap = new Map();
                snapTeam.docs.forEach(d => devMap.set(d.id, d.data()));
                snapSelf.docs.forEach(d => devMap.set(d.id, d.data()));
                allDeviations = Array.from(devMap.values());
            } else {
                const qSelf = query(devRef, where("userId", "==", userProfile.id));
                const snap = await getDocs(qSelf);
                allDeviations = snap.docs.map(d => d.data());
            }

            // 4. Calculate Stats with "Active During Period" Logic
            const dateRange = getDatesInRange(filterStart, filterEnd);
            const reportStartDate = new Date(filterStart);
            const reportEndDate = new Date(filterEnd);
            reportEndDate.setHours(23, 59, 59, 999); // End of day

            const stats = targetUsers.reduce((acc, user) => {
                // --- ✅ NEW FILTER LOGIC ---
                
                // 1. Join Date Check
                // If createdAt is missing, assume they are old employees (allow them)
                let joinedAt = new Date(0); 
                if (user.createdAt && user.createdAt.seconds) {
                    joinedAt = new Date(user.createdAt.seconds * 1000);
                } else if (user.createdAt instanceof Date) {
                    joinedAt = user.createdAt;
                }

                // If user joined AFTER the report period ended, exclude them.
                if (joinedAt > reportEndDate) return acc;

                // 2. Exit Date Check (for Deactivated users)
                // We use 'updatedAt' as a proxy for 'deactivatedAt' since AdminModule updates it on deactivation.
                if (user.status === 'Deactivated') {
                    let leftAt = null;
                    if (user.updatedAt && user.updatedAt.seconds) {
                        leftAt = new Date(user.updatedAt.seconds * 1000);
                    } else if (user.updatedAt instanceof Date) {
                        leftAt = user.updatedAt;
                    }

                    // If user left BEFORE the report period started, exclude them.
                    if (leftAt && leftAt < reportStartDate) return acc;
                }
                // --- END FILTER LOGIC ---

                let expectedWFO = 0;
                let actualWFO = 0;

                dateRange.forEach(date => {
                    if (isWeekend(date)) return;
                    const dateStr = date.toISOString().split('T')[0];
                    const dayName = getDayName(date);

                    const sched = allSchedules
                        .filter(s => s.assignedUsers.includes(user.id))
                        .find(s => dateStr >= s.startDate && dateStr <= s.endDate);
                    
                    const baseStatus = sched ? (sched.pattern[dayName] || 'WFH') : 'WFH';
                    const dev = allDeviations.find(d => d.userId === user.id && d.date === dateStr && d.status === 'Approved');
                    const comp = allDeviations.find(d => d.userId === user.id && d.compensatoryDate === dateStr && d.status === 'Approved');

                    let finalStatus = baseStatus;
                    if (dev) finalStatus = dev.type;
                    if (comp) finalStatus = 'WFO';

                    if (baseStatus === 'WFO') expectedWFO++;
                    if (finalStatus === 'WFO') actualWFO++;
                });

                acc.push({
                    name: user.name,
                    dept: user.department,
                    status: user.status, // Useful to show visual indicator
                    expected: expectedWFO,
                    actual: actualWFO,
                    variance: actualWFO - expectedWFO
                });
                
                return acc;
            }, []);

            setReportData(stats);

        } catch (e) { 
            console.error("Report Error:", e); 
            alert("Error loading report. Please check console.");
        } finally { 
            setLoading(false); 
        }
    };

    // 5. Sort & Filter
    const processedData = useMemo(() => {
        let data = [...reportData];

        if (searchTerm) {
            const lowerQ = searchTerm.toLowerCase();
            data = data.filter(row => 
                row.name.toLowerCase().includes(lowerQ) || 
                (row.dept && row.dept.toLowerCase().includes(lowerQ))
            );
        }

        data.sort((a, b) => a.name.localeCompare(b.name));

        return data;
    }, [reportData, searchTerm]);

    return (
        <div>
            {/* Controls Bar */}
            <div className="flex flex-col md:flex-row gap-4 items-end justify-between mb-6 bg-gray-50 p-4 rounded-lg">
                <div className="flex flex-wrap gap-4 items-end w-full md:w-auto">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">From</label>
                        <input type="date" value={filterStart} onChange={e => setFilterStart(e.target.value)} className="p-2 border rounded w-full md:w-auto text-sm" />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">To</label>
                        <input type="date" value={filterEnd} onChange={e => setFilterEnd(e.target.value)} className="p-2 border rounded w-full md:w-auto text-sm" />
                    </div>
                    <button onClick={generateReport} className="px-6 py-2 bg-[#213368] text-white rounded font-bold hover:opacity-90 text-sm">
                        Run Report
                    </button>
                </div>

                {/* Search Bar */}
                <div className="w-full md:w-64 relative">
                    <SearchIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input 
                        type="text" 
                        placeholder="Search employee..." 
                        className="w-full pl-9 pr-4 py-2 border rounded text-sm focus:ring-2 focus:ring-[#20BEC6] outline-none"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {loading ? <LoadingSpinner /> : (
                <div className="overflow-x-auto border rounded-lg shadow-sm">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Employee</th>
                                <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 uppercase">Expected WFO</th>
                                <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 uppercase">Actual WFO</th>
                                <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 uppercase">Variance</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {processedData.length === 0 ? (
                                <tr><td colSpan="4" className="p-8 text-center text-gray-400 italic">No data found. Click "Run Report" or refine search.</td></tr>
                            ) : (
                                processedData.map((row, i) => (
                                    <tr key={i} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center">
                                                <div>
                                                    <div className="text-sm font-bold text-[#213368]">
                                                        {row.name}
                                                        {row.status === 'Deactivated' && <span className="ml-2 text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium">Inactive</span>}
                                                    </div>
                                                    <div className="text-xs text-gray-400">{row.dept || 'Unassigned'}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center text-sm text-gray-600 font-medium">{row.expected}</td>
                                        <td className="px-6 py-4 text-center text-sm font-bold text-[#213368]">{row.actual}</td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-bold w-16
                                                ${row.variance < 0 ? 'bg-red-100 text-red-700' : 
                                                  row.variance > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}
                                            `}>
                                                {row.variance > 0 ? `+${row.variance}` : row.variance}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}